import { useState, useEffect } from 'react';
import { Pencil, Save, X, Plus, Trash2, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import axiosClient from '../../api/axiosClient';

export default function PlanEditor() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const res = await axiosClient.get('/admin/plans');
      const normalizedPlans = (res.data.plans ?? []).map(plan => {
        let parsedFeatures = [];
        if (Array.isArray(plan.features)) {
          parsedFeatures = plan.features;
        } else if (typeof plan.features === 'string') {
          try { parsedFeatures = JSON.parse(plan.features); } catch(e) {}
        }
        return {
          ...plan,
          features: parsedFeatures,
          price: plan.price ?? ''
        };
      });
      setPlans(normalizedPlans);
    } catch (err) {
      console.error('Failed to fetch plans', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (plan) => {
    setEditingPlan(JSON.parse(JSON.stringify(plan))); // Deep copy
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axiosClient.put(`/admin/plans/${editingPlan.id}`, {
        name: editingPlan.name,
        price: editingPlan.price,
        period: editingPlan.period,
        description: editingPlan.description,
        features: editingPlan.features
      });
      // Update local state with normalized features
      setPlans(plans.map(p => {
        if (p.id !== res.data.plan.id) return p;
        let parsedFeatures = [];
        if (Array.isArray(res.data.plan.features)) {
          parsedFeatures = res.data.plan.features;
        } else if (typeof res.data.plan.features === 'string') {
          try { parsedFeatures = JSON.parse(res.data.plan.features); } catch(e) {}
        }
        return {
          ...res.data.plan,
          features: parsedFeatures,
          price: res.data.plan.price ?? ''
        };
      }));
      setEditingPlan(null);
    } catch (err) {
      toast.error('Failed to save plan details');
    } finally {
      setSaving(false);
    }
  };

  const addFeature = () => {
    setEditingPlan({
      ...editingPlan,
      features: [...editingPlan.features, "New Feature"]
    });
  };

  const updateFeature = (index, value) => {
    const newFeatures = [...editingPlan.features];
    newFeatures[index] = value;
    setEditingPlan({ ...editingPlan, features: newFeatures });
  };

  const removeFeature = (index) => {
    const newFeatures = [...editingPlan.features];
    newFeatures.splice(index, 1);
    setEditingPlan({ ...editingPlan, features: newFeatures });
  };

  if (loading) {
    return (
      <div className="flex justify-center p-12">
        <Loader2 className="w-8 h-8 text-primary-600 dark:text-primary-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-3 gap-6">
        {plans.map(plan => (
          <div key={plan.id} className={`bg-white dark:bg-[#1e2336] border rounded-2xl p-6 shadow-sm relative ${plan.popular ? 'border-primary-500 ring-2 ring-primary-500/20' : 'border-slate-200 dark:border-slate-700'}`}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white">{plan.name}</h3>
                <div className="flex items-baseline gap-1 mt-1">
                  <span className="text-2xl font-bold text-slate-900 dark:text-white">₹{plan.price ? plan.price.toString().replace(/[^0-9.]/g, '') : ''}</span>
                  <span className="text-sm text-slate-500 dark:text-slate-400">{plan.period}</span>
                </div>
              </div>
              <button 
                onClick={() => handleEditClick(plan)}
                className="p-2 bg-slate-50 dark:bg-[#0b1220] hover:bg-slate-100 dark:hover:bg-slate-700/50 dark:bg-slate-800 rounded-lg text-slate-600 dark:text-slate-300 transition-colors"
                title="Edit Plan"
              >
                <Pencil size={18} />
              </button>
            </div>
            
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-4 h-10">{plan.description}</p>
            
            <div className="text-sm font-semibold text-slate-900 dark:text-white mb-2">Features ({plan.features.length})</div>
            <ul className="space-y-2 mb-4 h-48 overflow-y-auto pr-2 text-sm text-slate-600 dark:text-slate-300">
              {plan.features.map((f, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-primary-500">•</span> {f}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      {editingPlan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-[#1e2336] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b border-slate-100 dark:border-slate-800">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white">Edit Plan: {editingPlan.name}</h3>
              <button onClick={() => setEditingPlan(null)} className="text-slate-400 hover:text-slate-600 dark:text-slate-300">
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              <form id="edit-plan-form" onSubmit={handleSave} className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Plan Name</label>
                    <input required type="text" value={editingPlan.name} onChange={e => setEditingPlan({...editingPlan, name: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Price</label>
                      <input required type="text" value={editingPlan.price} onChange={e => setEditingPlan({...editingPlan, price: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Period</label>
                      <input required type="text" value={editingPlan.period} onChange={e => setEditingPlan({...editingPlan, period: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" />
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Description</label>
                  <textarea required value={editingPlan.description} onChange={e => setEditingPlan({...editingPlan, description: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 outline-none h-20" />
                </div>
                
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">Features</label>
                    <button type="button" onClick={addFeature} className="text-xs font-medium text-primary-600 dark:text-primary-500 bg-primary-50 dark:bg-primary-900/20 px-2 py-1 rounded flex items-center hover:bg-primary-100">
                      <Plus size={14} className="mr-1"/> Add Feature
                    </button>
                  </div>
                  <div className="space-y-2">
                    {editingPlan.features.map((feature, i) => (
                      <div key={i} className="flex gap-2">
                        <input required type="text" value={feature} onChange={e => updateFeature(i, e.target.value)}
                          className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 outline-none text-sm" />
                        <button type="button" onClick={() => removeFeature(i)} className="p-2 text-red-500 hover:bg-red-50 dark:bg-red-900/20 rounded-lg">
                          <Trash2 size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </form>
            </div>
            
            <div className="p-6 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-[#0b1220] flex justify-end gap-3">
              <button type="button" onClick={() => setEditingPlan(null)} className="px-4 py-2 font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200 rounded-lg transition-colors">
                Cancel
              </button>
              <button type="submit" form="edit-plan-form" disabled={saving} className="px-6 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors flex items-center disabled:opacity-70">
                {saving ? <Loader2 size={18} className="animate-spin mr-2" /> : <Save size={18} className="mr-2" />}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
